import { QueryClient } from '@tanstack/react-query';
import React from 'react';

import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { Messages } from '~src/codegen/Localize';

import { RoutingFilterResponse } from '../../serverApi/requests/RoutingFilterTypes';
import { RoutingFilter } from '../../serverApi/resources/RoutingFilter';
import { PoolAssignmentOption } from '../types/PoolAssignmentOption';
import { PoolID } from '../types/PoolID';
import { RoutingFilterItem } from '../types/RoutingFilterItem';
import { RoutingFilterPermission } from '../types/RoutingFilterPermission';
import { collectConflictingProjectIds, getRoutingFilterErrorMessage } from '../utils/RoutingFilterErrorUtilities';
import { getRoutingFilterWarnings } from '../utils/RoutingFilterWarningUtilities';
import { openDeleteRoutingFilterConfirmationDialog } from './deleteRoutingFilterConfirmationDialog/DeleteRoutingFilterConfirmationDialog';
import { CreateRoutingFilterPreFill, openCreateRoutingFilterDialog, openEditRoutingFilterDialog, RoutingFilterDialogConflictOptions } from './routingFilterDialog/RoutingFilterDetailsDialog';
import { ConflictError, UnsavedFilter } from './RoutingFiltersConflictContext';
import { getRoutingFiltersQueryKey } from './useRoutingFilters';

// Inputs the mutation/CRUD callbacks need from the host component. The unsaved
// filter state stays in BridgeRoutingFilters (routingFilters is derived from it,
// and these handlers read that derived list — keeping state and derivation in the
// component avoids a hook-input cycle), so it is threaded in here along with its
// setters.
export type UseRoutingFilterHandlersArgs = Readonly<{
  queryClient: QueryClient;
  siteId: string | undefined;
  rawFilters: ReadonlyArray<RoutingFilterResponse>;
  routingFilters: ReadonlyArray<RoutingFilterItem>;
  poolOptions: ReadonlyArray<PoolAssignmentOption>;
  warnedDomains: ReadonlySet<string>;
  unsavedFilter: UnsavedFilter | null;
  setUnsavedFilter: React.Dispatch<React.SetStateAction<UnsavedFilter | null>>;
  setConflictsHighlighted: React.Dispatch<React.SetStateAction<boolean>>;
}>;

// Only the callbacks the component wires into context are returned; onConflict
// stays internal (it is passed straight to the create/edit dialogs here).
export type RoutingFilterHandlers = Readonly<{
  onClearUnspecifiedProjectsClicked: (filterId: string) => Promise<boolean>;
  onEditFilterClicked: (filterId: string) => void;
  onAddFilterClicked: (preFill?: CreateRoutingFilterPreFill) => void;
  onDeleteFilterClicked: (filterId: string) => void;
}>;

// Owns the routing-filter create/edit/delete operations: the write-through to
// the React Query cache and the CRUD click handlers that drive the create/edit
// dialogs and the delete confirmation. Extracted from BridgeRoutingFilters so the
// component is left with view state, derivation, and rendering.
export function useRoutingFilterHandlers(args: UseRoutingFilterHandlersArgs): RoutingFilterHandlers {
  const {
    queryClient, siteId, rawFilters, routingFilters, poolOptions, warnedDomains,
    unsavedFilter, setUnsavedFilter, setConflictsHighlighted,
  } = args;

  const onConflict = React.useCallback((filter: RoutingFilterResponse, isNew: boolean, errors: ReadonlyArray<ConflictError>) => {
    const conflictingFilterIds = new Set<string>();
    for (const err of errors) {
      if (err.routingRuleId) {
        conflictingFilterIds.add(err.routingRuleId);
      }
    }
    setUnsavedFilter({ filter, isNew, conflictErrors: errors, conflictingFilterIds });
    setConflictsHighlighted(false);
  }, [setUnsavedFilter, setConflictsHighlighted]);

  // Invariant: these three cache write-throughs are only ever reached from a
  // handler that has already early-returned on !siteId (onEdit/onAdd/onDelete and
  // onClearUnspecifiedProjectsClicked all guard first), so siteId is defined by
  // the time the query key is built. Keep that guard in any new caller.
  const updateFilterInCache = React.useCallback((updatedFilter: RoutingFilterResponse) => {
    setUnsavedFilter(null);
    queryClient.setQueryData<ReadonlyArray<RoutingFilterResponse>>(
      getRoutingFiltersQueryKey(siteId),
      (prev) => prev ? prev.map(f => f.id === updatedFilter.id ? updatedFilter : f) : []
    );
    ToasterService.instance.pop({
      type: ToastType.Success,
      text: Messages.bridgeSiteSettingsEditRoutingFilterSuccess(),
    });
  }, [queryClient, siteId, setUnsavedFilter]);

  const addFilterToCache = React.useCallback((newFilter: RoutingFilterResponse) => {
    setUnsavedFilter(null);
    queryClient.setQueryData<ReadonlyArray<RoutingFilterResponse>>(
      getRoutingFiltersQueryKey(siteId),
      (prev) => prev ? [...prev, newFilter] : [newFilter]
    );
    ToasterService.instance.pop({
      type: ToastType.Success,
      text: Messages.bridgeSiteSettingsCreateRoutingFilterSuccess(),
    });
  }, [queryClient, siteId, setUnsavedFilter]);

  const removeFilterFromCache = React.useCallback((filterId: string) => {
    queryClient.setQueryData<ReadonlyArray<RoutingFilterResponse>>(
      getRoutingFiltersQueryKey(siteId),
      (prev) => prev ? prev.filter(f => f.id !== filterId) : []
    );
    ToasterService.instance.pop({
      type: ToastType.Success,
      text: Messages.bridgeSiteSettingsDeleteRoutingFilterSuccess(),
    });
  }, [queryClient, siteId]);

  // Resolves true when the deleted-project references were successfully cleared,
  // so callers (e.g. the edit dialog) can update their own view in place rather
  // than waiting for a reopen. Resolves false on any error or no-op.
  const onClearUnspecifiedProjectsClicked = React.useCallback((filterId: string): Promise<boolean> => {
    if (!siteId) {
      return Promise.resolve(false);
    }
    const serverFilter = rawFilters.find(f => f.id === filterId);
    if (!serverFilter) {
      return Promise.resolve(false);
    }
    const deletedProjectIds = (serverFilter.projects ?? []).filter(p => p.deleted).map(p => p.id);
    if (deletedProjectIds.length === 0) {
      return Promise.resolve(false);
    }
    return RoutingFilter.editRoutingFilter({
      siteId,
      routingRuleId: filterId,
      routingRuleChanges: { projects: { add: [], remove: deletedProjectIds } },
    }).then(
      (result) => {
        if (result.error) {
          ToasterService.instance.pop({
            type: ToastType.Error,
            text: getRoutingFilterErrorMessage(result.error.errorCode),
          });
          return false;
        }
        if (result.success) {
          updateFilterInCache(result.success.routingRule);
          return true;
        }
        return false;
      }
    ).catch(
      () => {
        ToasterService.instance.pop({
          type: ToastType.Error,
          text: Messages.bridgeSiteSettingsEditRoutingFilterError(),
        });
        return false;
      }
    );
  }, [siteId, rawFilters, updateFilterInCache]);

  const onEditFilterClicked = React.useCallback((filterId: string) => {
    if (!siteId) {
      return;
    }

    const filter = routingFilters.find((f: RoutingFilterItem) => f.id === filterId);
    if (!filter) {
      return;
    }

    if (unsavedFilter && filterId === unsavedFilter.filter.id) {
      const permission = filter.isBlocked ? RoutingFilterPermission.BLOCK : RoutingFilterPermission.ALLOW;
      const initialProjectLuids = filter.projects.map(p => p.id);

      // Reproduce the prior save attempt's conflict/warning surface inside the
      // reopened dialog: project highlights from the conflict errors, the
      // missing-domain-wide warning (same predicate as the row badge), and the
      // deleted-project clear action.
      const conflictingProjectLuids = collectConflictingProjectIds(unsavedFilter.conflictErrors);
      const { hasMissingDomainWideWarning: showMissingDomainWideWarning, hasDeletedProjects } = getRoutingFilterWarnings(filter, warnedDomains);
      const conflict = {
        conflictErrors: unsavedFilter.conflictErrors,
        conflictingProjectLuids: Array.from(conflictingProjectLuids),
        showMissingDomainWideWarning,
        hasDeletedProjects,
        onClearDeletedProjects: hasDeletedProjects ? () => onClearUnspecifiedProjectsClicked(filter.id) : undefined,
      };

      if (unsavedFilter.isNew) {
        openCreateRoutingFilterDialog({
          siteId,
          poolOptions,
          otherDomainNames: [],
          dataConnectDomainNames: [],
          initialProjectLuids,
          preFill: {
            domainName: filter.domain,
            poolID: filter.poolId ? filter.poolId as PoolID : null,
            permission,
          },
          onSuccess: addFilterToCache,
          onConflict,
          conflict,
        });
      } else {
        const serverFilter = rawFilters.find(f => f.id === filterId);
        const savedProjectLuids = serverFilter
          ? (serverFilter.projects ?? []).map(p => p.id)
          : [];
        openEditRoutingFilterDialog({
          filterId: filter.id,
          siteId,
          domainName: filter.domain,
          permission,
          poolID: filter.poolId ? filter.poolId as PoolID : null,
          initialProjectLuids,
          savedProjectLuids,
          poolOptions,
          otherDomainNames: [],
          dataConnectDomainNames: [],
          onSuccess: updateFilterInCache,
          onConflict,
          conflict,
        });
      }
      return;
    }

    const permission = filter.isBlocked ? RoutingFilterPermission.BLOCK : RoutingFilterPermission.ALLOW;
    const poolId: PoolID | null = filter.poolId ? filter.poolId as PoolID : null;
    const initialProjectLuids = filter.projects.map(p => p.id);

    // Surface the same warnings the table row shows (missing domain-wide rule,
    // deleted projects) inside the edit dialog. There is no conflict on a plain
    // edit, so conflictErrors/conflictingProjectLuids are empty.
    const { hasMissingDomainWideWarning: showMissingDomainWideWarning, hasDeletedProjects } = getRoutingFilterWarnings(filter, warnedDomains);
    const warningOptions: RoutingFilterDialogConflictOptions | undefined = (showMissingDomainWideWarning || hasDeletedProjects)
      ? {
          conflictErrors: [],
          conflictingProjectLuids: [],
          showMissingDomainWideWarning,
          hasDeletedProjects,
          onClearDeletedProjects: hasDeletedProjects ? () => onClearUnspecifiedProjectsClicked(filter.id) : undefined,
        }
      : undefined;

    openEditRoutingFilterDialog({
      filterId: filter.id,
      siteId,
      domainName: filter.domain,
      permission,
      poolID: poolId,
      initialProjectLuids,
      poolOptions,
      otherDomainNames: [],
      dataConnectDomainNames: [],
      onSuccess: updateFilterInCache,
      onConflict,
      conflict: warningOptions,
    });
  }, [routingFilters, rawFilters, poolOptions, siteId, updateFilterInCache, addFilterToCache, onConflict, onClearUnspecifiedProjectsClicked, warnedDomains, unsavedFilter]);

  const onAddFilterClicked = React.useCallback((preFill?: CreateRoutingFilterPreFill) => {
    if (!siteId) {
      return;
    }
    openCreateRoutingFilterDialog({
      siteId,
      poolOptions,
      otherDomainNames: [],
      dataConnectDomainNames: [],
      preFill,
      onSuccess: addFilterToCache,
      onConflict,
    });
  }, [poolOptions, siteId, addFilterToCache, onConflict]);

  const onDeleteFilterClicked = React.useCallback((filterId: string) => {
    if (!siteId) {
      return;
    }
    if (unsavedFilter && unsavedFilter.isNew && unsavedFilter.filter.id === filterId) {
      return;
    }
    openDeleteRoutingFilterConfirmationDialog(() => {
      RoutingFilter.deleteRoutingFilter({ siteId, routingRuleId: filterId }).then(
        (result) => {
          if (result.error) {
            ToasterService.instance.pop({
              type: ToastType.Error,
              text: getRoutingFilterErrorMessage(result.error.errorCode),
            });
            return;
          }
          removeFilterFromCache(filterId);
          if (unsavedFilter && unsavedFilter.filter.id === filterId) {
            setUnsavedFilter(null);
          } else if (unsavedFilter && unsavedFilter.conflictingFilterIds.has(filterId)) {
            const remainingErrors = unsavedFilter.conflictErrors.filter(e => e.routingRuleId !== filterId);
            const remainingIds = new Set(unsavedFilter.conflictingFilterIds);
            remainingIds.delete(filterId);
            if (remainingErrors.length === 0) {
              setUnsavedFilter(null);
            } else {
              setUnsavedFilter({ ...unsavedFilter, conflictErrors: remainingErrors, conflictingFilterIds: remainingIds });
            }
          }
        }
      ).catch(
        () => {
          ToasterService.instance.pop({
            type: ToastType.Error,
            text: Messages.bridgeSiteSettingsDeleteRoutingFilterError(),
          });
        }
      );
    });
  }, [siteId, removeFilterFromCache, unsavedFilter, setUnsavedFilter]);

  return {
    onClearUnspecifiedProjectsClicked,
    onEditFilterClicked,
    onAddFilterClicked,
    onDeleteFilterClicked,
  };
}
